import { getId, Modal, PrimaryButton, ProgressIndicator } from "@fluentui/react";
import { useRef, useEffect } from "react";
import { CartItemInfo } from "../../../carts/models/CartModel";
import useCartVM from "../../../carts/viewmodels/CartVM";
import { primaryButtonStyles } from "../../../common/views/DefaultStyles";
import ModalHeaderView from "../../../common/views/ModalHeaderView";
import { StyleUtils } from "../../../common/views/StyleUtils";
import { Utils } from "../../../common/views/Utils";
import { AddressModel } from "../../../customers/models/AddressModel";
import { PhoneModel } from "../../../customers/models/PhoneModel";
import CustomerAddressView from "../../../customers/views/addresses/CustomerAddressView";
import CustomerPhoneView from "../../../customers/views/phones/CustomerPhoneView";
import InvoicePrintView from "../../../invoices/views/shared/InvoicePrintView";
import { AppliedPaymentModel, PaymentType } from "../../../payments/models/PaymentModels";
import { ProductUtils } from "../../../products/ProductUtils";
import { CheckoutSummaryModel } from "../../models/OrderModel";
import useCheckoutVM from "../../viewmodels/CheckoutVM";
import { OrderUtils } from "../shared/OrderUtils";
import OrderConfirmationView from "./OrderConfirmationView";

export type CheckoutProps = {
    cartId: number;
}

export default function CheckoutView({ cartId }: CheckoutProps) {
    const hasLoaded = useRef(false);

    const vm = useCheckoutVM(cartId);
    const cartVM = useCartVM(cartId);

    useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.getSummary();
        cartVM.onLoad();
    }, [vm, cartVM]);

    const width4 = Utils.getColumnWidth(4);
    const width8 = Utils.getColumnWidth(8);
    const width6 = Utils.getColumnWidth(6);

    const isMobile = window.innerWidth <= 500;
    const titleId: string = getId('title');
    const subtitleId: string = getId('subText');

    const onSelectAddress = (address: AddressModel) => {
        if (vm.summary) {
            vm.summary.address = address;
            //vm.setSummary(prev => ({ ...prev, address: address }));
        }

        vm.setShowAddressBook(false);
    }

    const onCloseAddress = () => {
        vm.setShowAddressBook(false);
    }

    const onSelectPhone = (phone: PhoneModel) => {
        if (vm.summary) {
            vm.summary.phone = phone;
        }

        vm.setShowPhoneBook(false);
    }

    const onClosePhone = () => {
        vm.setShowPhoneBook(false);
    }

    const printRow = (label: string, value: string, css: string = "") => {
        return <div className={"ms-Grid-row pt-10 " + css}>
            <div className={width8 + " left"}>{label}:</div>
            <div className={width4 + " right"}>{value}</div>
        </div>;
    }

    const phoneBook = (summary: CheckoutSummaryModel) => {
        var phone = "";

        if (summary.phone) {
            phone = Utils.formatPhone2(summary.phone.countryCode, summary.phone.phoneNo);
        }

        return <div className="default-border pv-10 ph-15">
            <div className="ms-Grid-row">
                <div className={width6 + " font-bold left"}>Phone</div>
                <div className={width6 + " right font-smaller"}>
                    <a onClick={() => vm.setShowPhoneBook(true)} style={{ textDecoration: "underline" }}>
                        Phone Book
                    </a>
                </div>
            </div>

            {phone && <div className="pt-10 left">{phone}</div>}
        </div>;
    }

    const cityStateZipText = (address: AddressModel) => {
        var text = address.city ?? "";

        if (!Utils.isNull(address.state)) {
            text += (!Utils.isNullOrEmpty(text) ? ", " : "") + address.state?.nameKh;
        }

        if (!Utils.isNullOrEmpty(address.zip)) {
            text += (!Utils.isNullOrEmpty(text) ? " " : "") + address.zip;
        }

        if (Utils.isNullOrEmpty(text)) {
            return <></>;
        }

        return <div className="pt-5">
            <div>{text}</div>
        </div>
    }

    const addressBook = (summary: CheckoutSummaryModel) => {
        const address = summary.address;

        return <div className="default-border pv-10 ph-15">
            <div className="ms-Grid-row">
                <div className={width6 + " font-bold left"}>Address</div>
                <div className={width6 + " right font-smaller"}>
                    <a onClick={() => vm.setShowAddressBook(true)} style={{ textDecoration: "underline" }}>
                        Address Book
                    </a>
                </div>
            </div>

            {address && <div className="pt-10 left">
                <div>{address.address1}</div>
                {!Utils.isNullOrEmpty(address.address2) && <div className="pt-5">{address.address2}</div>}
                {cityStateZipText(address)}
            </div>}
        </div>;
    }

    const itemsBlock = () => {
        const cart = cartVM.cart;

        if (!cart) return <></>;

        return <div className="default-border pt-10 pb-20 ph-15">
            <div className="font-title left">Items</div>

            {cart.items.map((item: CartItemInfo, index: number) => {
                let url = ProductUtils.getImageUrl(item.product.imageUrl);

                return <div key={item.id} className="pt-10" style={{ width: 80, display: "inline-flex" }}>
                    {OrderUtils.image(url, item.qty, 60, 0, false)}
                </div>
            })}
        </div>;
    }

    const summaryBlock = (summary: CheckoutSummaryModel) => {
        return <div className="ms-Grid default-border pv-10 ph-15">
            <div className="font-bold left">Order Summary</div>

            {printRow("Items", summary.count.toString())}
            {printRow("Amount", "$ " + summary.amount.toFixed(2))}
            {printRow("Shipping Fee", "$ " + summary.shipping.toFixed(2))}

            {summary.payments.length > 0 && <>
                {vm.getTotalPointsPaymentAmount() > 0 && <>
                    {printRow(
                        "Used Earning Points",
                        "$ " + vm.getTotalPointsPaymentAmount().toFixed(2), "firmed"
                    )}
                </>}

                {vm.getPaymentWithoutPoints().map((payment: AppliedPaymentModel, index?: number) => {
                    return <>
                        {printRow(PaymentType[payment.type], "$ " + (-1 * payment.amount).toFixed(2))}

                        {!Utils.isNullOrEmpty(payment.note) && <div className="font-smaller firmed">
                            {payment.note}
                        </div>}
                    </>;
                })}
            </>}

            {printRow("Order Total", "$ " + summary.balance.toFixed(2))}
        </div>;
    }

    const selectAddressBook = () => {
        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={true}
                onDismiss={onCloseAddress}
                styles={StyleUtils.getModalWidthStyles(450)}
                isBlocking={false}
                isModeless={false}
            >
                <ModalHeaderView title="Invoice Details" onClose={onCloseAddress} />

                <div
                    id={subtitleId}
                    className={(isMobile ? "mobile-" : "") + "popup-body ph-0"}
                    style={{ maxHeight: "90vh", overflowX: "hidden" }}
                >
                    <CustomerAddressView onSelect={onSelectAddress} />
                </div>
            </Modal>
        </>;
    }

    const selectPhoneBook = () => {
        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={vm.showPhoneBook}
                onDismiss={onClosePhone}
                styles={StyleUtils.getModalWidthStyles(450)}
                isBlocking={false}
                isModeless={false}
            >
                <ModalHeaderView title="Phone Book" onClose={onClosePhone} />

                <div
                    id={subtitleId}
                    className={(isMobile ? "mobile-" : "") + "popup-body ph-0"}
                    style={{ maxHeight: "90vh", overflowX: "hidden" }}
                >
                    <CustomerPhoneView onSelect={onSelectPhone} />
                </div>
            </Modal>
        </>;
    }

    const submitButton = () => {
        return <>
            <PrimaryButton
                onClick={vm.submitOrder}
                style={{ width: "100%" }}
                styles={primaryButtonStyles}
            >Place Order</PrimaryButton>
        </>;
    }

    const mainContent = () => {
        return <>
            <div className="font-title left">Checkout</div>

            {vm.summary && <>
                {/*<div className="ms-Grid-row">
                <div className={width6D + " pt-20"}>{phoneBook(vm.summary)}</div>
                <div className={width6D + " pt-20"}>{addressBook(vm.summary)}</div>
            </div>*/}

                <div className={"pt-20"}>{phoneBook(vm.summary)}</div>
                <div className={"pt-20"}>{addressBook(vm.summary)}</div>
                <div className="pt-20">{itemsBlock()}</div>
                <div className="pt-20">{summaryBlock(vm.summary)}</div>

                {!vm.submitting && <div className="pt-20">{submitButton()}</div>}

                {vm.submitting && <div className="pt-20">
                    <ProgressIndicator label="Submitting" description="" />
                </div>}
            </>}
        </>;
    }

    return <div className="p-10" style={{ maxWidth: 800 }}>
        {!vm.showConfirmation && mainContent()}
        {vm.showConfirmation && vm.confirmedModel && <OrderConfirmationView model={vm.confirmedModel} />}
        {vm.showAddressBook && selectAddressBook()}
        {selectPhoneBook()}
    </div>;
}